import { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    IconButton,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import classNames from 'classnames';

import { Icon } from '../shared/Icon';
import {
    MetaForm,
    PortalTranslationsEditModal,
} from './PortalTranslationsEditModal';
import { LanguageType } from '@lifeserver/shared';

import {
    PortalLocalizationMetaDataDTO,
    usePortalLocalizationWithMetaData,
    useSavePortalLocalization,
} from '../hooks/portal';

import styles from './PortalTranslations.module.scss';
import { downloadAllXliffAsZip, parseXliff } from './translationUtils';

const ROWS_PER_PAGE = 15;
export const PortalTranslations = () => {
    const { data: localizations = [], refetch } =
        usePortalLocalizationWithMetaData();
    const { mutateAsync: mutateLocalization, isPending: isSaving } =
        useSavePortalLocalization();

    const [modalInitialValues, setModalInitialValues] = useState<{
        localizationId: string;
        metaForm: MetaForm;
        localizationForm: Record<string, string>;
        metadataId?: string | null;
    }>({
        localizationId: '',
        metaForm: { characterLimit: 0, notes: '' },
        localizationForm: {},
        metadataId: null,
    });
    const [modalOpen, setModalOpen] = useState(false);

    const [visibleLanguages, setVisibleLanguages] = useState<string[]>(['en']);

    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    type LocalizationWithPosts = {
        id: string;
        language: string;
        translation: string;
        postLocalizations?: { portalPostId: string }[];
    };

    type LocalizationGroup = {
        localizations: LocalizationWithPosts[];
        metadata?: PortalLocalizationMetaDataDTO['metadata'];
    };
    const grouped = (localizations as LocalizationGroup[]).reduce(
        (acc, group) => {
            if (
                !group.localizations ||
                !Array.isArray(group.localizations) ||
                group.localizations.length === 0
            ) {
                return acc;
            }
            const id = group.localizations[0]?.id;
            if (!id) return acc;

            const postIds = [
                ...new Set(
                    group.localizations.flatMap((loc) =>
                        (loc.postLocalizations || []).map(
                            (pl) => pl.portalPostId,
                        ),
                    ),
                ),
            ];
            acc[id] = acc[id] || {
                translations: {},
                metadata: group.metadata,
                postIds,
            };
            group.localizations.forEach((loc) => {
                acc[id].translations[loc.language] = loc.translation;
            });
            return acc;
        },
        {} as Record<
            string,
            {
                translations: Record<string, string>;
                metadata: PortalLocalizationMetaDataDTO['metadata'];
                postIds: string[];
            }
        >,
    );
    const filteredGrouped = Object.entries(grouped).filter(
        ([id, { translations }]) => {
            const searchLower = search.toLowerCase();
            if (id.toLowerCase().includes(searchLower)) return true;
            return Object.values(translations).some((text) =>
                text?.toLowerCase().includes(searchLower),
            );
        },
    );
    const pageCount = Math.ceil(filteredGrouped.length / ROWS_PER_PAGE);
    const pagedRows = filteredGrouped.slice(
        page * ROWS_PER_PAGE,
        page * ROWS_PER_PAGE + ROWS_PER_PAGE,
    );
    const allLanguages = Object.values(LanguageType).filter(
        (lang) => lang !== 'en',
    );

    const handleLanguageChange = (e: SelectChangeEvent<string[]>) => {
        const value = Array.isArray(e.target.value)
            ? e.target.value
            : e.target.value.split(',');
        if (!value.includes('en')) value.unshift('en');
        setVisibleLanguages([...new Set(value)]);
    };

    const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        setPage(0);
    };
    const handleEdit = (id: string) => {
        const meta = grouped[id]?.metadata;
        setModalInitialValues({
            localizationId: id,
            metaForm: {
                characterLimit: Number(meta?.characterLimit) || 0,
                notes: meta?.notes || '',
            },
            localizationForm: { ...grouped[id]?.translations },
            metadataId: meta?.id ?? null,
        });
        setModalOpen(true);
    };

    const handleAddLocalization = () => {
        setModalInitialValues({
            localizationId: '',
            metaForm: { characterLimit: 0, notes: '' },
            localizationForm: {},
            metadataId: null,
        });

        setModalOpen(true);
    };
    const handleLocalizationSave = async ({
        localizationId,
        metaForm,
        localizationForm,
    }: {
        localizationId: string;
        metaForm: MetaForm;
        localizationForm: Record<string, string>;
    }) => {
        if (!localizationId.trim()) return;

        const allTranslations = {
            ...grouped[localizationId]?.translations,
            ...localizationForm,
        };

        const existing = grouped[localizationId]?.metadata as
            | { characterLimit?: number; notes?: string; id?: string }
            | undefined;

        const metaData = {
            ...(existing?.id ? { id: existing.id } : {}),
            characterLimit: Number(metaForm.characterLimit) || 0,
            notes: metaForm.notes || '',
        };

        const localizationsToSave = Object.entries(allTranslations)
            .filter(
                ([, translation]) =>
                    translation && translation.trim().length > 0,
            )
            .map(([language, translation]) => ({
                id: localizationId,
                language,
                translation,
                metadataId: existing?.id ?? null,
                metaData,
            }));

        if (localizationsToSave.length === 0) return;

        await mutateLocalization({ localizations: localizationsToSave });

        setModalOpen(false);
    };
    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleImportFiles = async (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;
        const parsedFiles = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const text = await file.text();
            const parsed = parseXliff(text);
            parsedFiles.push(parsed);
        }

        const importedTranslations: Record<string, Record<string, string>> = {};

        parsedFiles.flat().forEach(({ id, language, translation }) => {
            if (!importedTranslations[id]) importedTranslations[id] = {};
            importedTranslations[id][language] = translation;
        });
        const allLocalizations = Object.entries(importedTranslations).flatMap(
            ([id, translations]) => {
                const existing = grouped[id]?.metadata as
                    | { characterLimit?: number; notes?: string; id?: string }
                    | undefined;
                const metaData = !existing?.id
                    ? {
                          characterLimit: Number(existing?.characterLimit) || 0,
                          notes: existing?.notes || '',
                      }
                    : undefined;

                return Object.entries(translations).map(
                    ([language, translation]) => ({
                        id,
                        language,
                        translation,
                        metadataId: existing?.id ?? null,
                        metaData,
                    }),
                );
            },
        );

        await mutateLocalization({ localizations: allLocalizations });
        await refetch();
    };

    useEffect(() => {}, [grouped]);

    return (
        <Box className={styles.outerContainer}>
            <Box className={styles.menu}>
                <Box className={styles.menuRow}>
                    <Box sx={{ minWidth: 240 }}>
                        <Typography
                            variant="subtitle2"
                            className={styles.label}
                        >
                            SEARCH
                        </Typography>
                        <TextField
                            className={styles.search}
                            variant="outlined"
                            size="small"
                            value={search}
                            onChange={handleSearchChange}
                            placeholder="Localization ID"
                        />
                    </Box>
                    <FormControl sx={{ minWidth: 220 }}>
                        <Typography
                            variant="subtitle2"
                            className={styles.label}
                        >
                            LANGUAGE
                        </Typography>
                        <Select
                            className={styles.select}
                            size="small"
                            multiple
                            value={visibleLanguages}
                            onChange={handleLanguageChange}
                            renderValue={(selected) =>
                                (selected as string[]).length === 0
                                    ? 'Välj språk att visa'
                                    : (selected as string[])
                                          .map(
                                              (code) =>
                                                  Object.entries(LanguageType)
                                                      .find(
                                                          ([, v]) => v === code,
                                                      )?.[0]
                                                      .replace(
                                                          /([A-Z])/g,
                                                          ' $1',
                                                      )
                                                      .trim() || code,
                                          )
                                          .join(', ')
                            }
                        >
                            {Object.entries(LanguageType).map(
                                ([name, code]) => (
                                    <MenuItem
                                        key={code}
                                        value={code}
                                        disabled={code === 'en'}
                                        className={styles.compactMenuItem}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={
                                                visibleLanguages.indexOf(code) >
                                                -1
                                            }
                                            disabled={code === 'en'}
                                        />
                                        <ListItemText
                                            className={styles.menuItemText}
                                            primary={name
                                                .replace(/([A-Z])/g, ' $1')
                                                .trim()}
                                        />
                                    </MenuItem>
                                ),
                            )}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={isSaving}
                        onClick={() => {
                            handleAddLocalization();
                        }}
                        startIcon={<Icon name="add" />}
                        style={{ display: 'none' }}
                    >
                        Add localization
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={isSaving}
                        onClick={() => {
                            handleImportClick();
                        }}
                    >
                        Import
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xliff,.xlf,application/xml,text/xml"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleImportFiles}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={isSaving}
                        onClick={async () => {
                            await downloadAllXliffAsZip(
                                localizations,
                                allLanguages,
                            );
                        }}
                    >
                        Export
                    </Button>
                </Box>
            </Box>
            <table
                style={{ borderCollapse: 'collapse', minWidth: 600 }}
                className={classNames('no-highlight', styles.table)}
            >
                <thead>
                    <tr>
                        <th style={{ width: '48px' }}></th>
                        <th className={styles.stickyIdCell}>Localization ID</th>
                        <th>Connected posts</th>
                        <th>EN</th>
                        {visibleLanguages
                            .filter((lang) => lang !== 'en')
                            .map((lang) => (
                                <th key={lang} style={{ padding: '0 12px' }}>
                                    {lang.toUpperCase()}
                                </th>
                            ))}
                    </tr>
                </thead>
                <tbody>
                    {pagedRows.map(([id, { translations, postIds }]) => (
                        <tr key={id}>
                            <td>
                                <IconButton
                                    onClick={() => handleEdit(id)}
                                    size="small"
                                >
                                    <Icon name="edit" />
                                </IconButton>
                            </td>
                            <td>{id}</td>
                            <td>
                                {postIds && postIds.length > 0 ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 0.5,
                                        }}
                                    >
                                        {postIds.map((postId) => (
                                            <Chip
                                                key={postId}
                                                label={postId}
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                ) : (
                                    <span className={styles.emptyCell}>–</span>
                                )}
                            </td>
                            <td>
                                <span>{translations['en'] || ''}</span>
                            </td>
                            {visibleLanguages
                                .filter((lang) => lang !== 'en')
                                .map((lang) => (
                                    <td
                                        key={lang}
                                        style={{ padding: '0 12px' }}
                                    >
                                        <span>{translations[lang] || ''}</span>
                                    </td>
                                ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            {/* Pagination */}
            <Box className={styles.paginationContainer}>
                <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                >
                    Föregående
                </button>
                <span>
                    Sida {page + 1} av {pageCount}
                </span>
                <button
                    onClick={() =>
                        setPage((p) => (p + 1 < pageCount ? p + 1 : p))
                    }
                    disabled={page + 1 >= pageCount}
                >
                    Nästa
                </button>
            </Box>
            <PortalTranslationsEditModal
                open={modalOpen}
                initialLocalizationId={modalInitialValues.localizationId}
                initialMetaForm={modalInitialValues.metaForm}
                initialLocalizationForm={modalInitialValues.localizationForm}
                onClose={() => setModalOpen(false)}
                onSave={handleLocalizationSave}
            />
        </Box>
    );
};
